export default function LabelPrintDialog({
  order,
  rows,
  excludedKeys,
  selectedCount,
  counts,
  countError,
  onCountChange,
  onToggle,
  onIncludeAll,
  onExcludeAll,
  onPreview,
  onPrint,
  onClose,
  busy = false,
  errorMessage = '',
  pricingNote = "DPrice and MRP use current market rates and this stage’s ledger margins. Replace old packet labels when dispatch prices change.",
}) {
  const selectedProducts = rows.filter((row) => !excludedKeys.has(row.key)).length;

  return (
    <div className="responsive-dialog fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div role="dialog" aria-modal="true" aria-label="Print Stock Labels" className="w-full max-w-4xl rounded-2xl bg-white shadow-2xl">
        <div className="flex flex-col gap-3 border-b p-4 md:flex-row md:items-start md:justify-between">
          <div>
            <h3 className="text-lg font-bold text-gray-900">Print Stock Labels</h3>
            <p className="text-sm text-gray-600">Edit Labels to print only the copies you need. For missed labels, exclude all, select the product, and enter the remaining count.</p>
            <p className="text-xs text-gray-600">{pricingNote}</p>
            <p className="text-sm text-gray-600">
              {order.dispatch_no || '-'} - {selectedCount} labels selected
            </p>
          </div>
          <button
            type="button"
            disabled={busy} onClick={onClose}
            className="self-start rounded-lg border px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
          >
            Close
          </button>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2 border-b bg-gray-50 px-4 py-3">
          <div className="text-sm font-semibold text-gray-700">
            {selectedProducts} of {rows.length} products included
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              disabled={busy} onClick={onIncludeAll}
              className="rounded-lg border px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-white"
            >
              Include All
            </button>
            <button
              type="button"
              disabled={busy} onClick={onExcludeAll}
              className="rounded-lg border px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-white"
            >
              Exclude All
            </button>
          </div>
        </div>

        <div className="max-h-[55vh] overflow-auto">
          <table className="min-w-full text-sm">
            <thead className="sticky top-0 bg-gray-100 text-gray-700">
              <tr>
                <th className="px-3 py-2 text-left">Print</th>
                <th className="px-3 py-2 text-left">Product</th>
                <th className="px-3 py-2 text-left">Barcode</th>
                <th className="px-3 py-2 text-left">Unit</th>
                <th className="px-3 py-2 text-center">Labels</th>
                <th className="px-3 py-2 text-right">MRP</th>
                <th className="px-3 py-2 text-right">MK Price</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const included = !excludedKeys.has(row.key);

                return (
                  <tr key={row.key} className={included ? 'border-t' : 'border-t bg-red-50 text-gray-500'}>
                    <td className="px-3 py-2">
                      <input
                        type="checkbox"
                        disabled={busy || row.disabled} checked={included}
                        onChange={() => onToggle(row.key)}
                        className="h-4 w-4"
                        aria-label={`Include ${row.productName}`}
                      />
                    </td>
                    <td className="px-3 py-2 font-semibold text-gray-900">{row.productName || '-'}{row.reason && <p className="text-xs text-red-700">{row.reason}</p>}</td>
                    <td className="px-3 py-2">{row.barcode || row.labelId || '-'}</td>
                    <td className="px-3 py-2">{row.unit || '-'}</td>
                    <td className="px-3 py-2 text-center">
                      <input
                        type="number"
                        min="0"
                        max={row.count}
                        step="1"
                        value={counts[row.key] ?? row.count}
                        disabled={busy || row.disabled || !included}
                        onChange={event => onCountChange(row.key, event.target.value)}
                        aria-label={`Label count for ${row.productName} ${row.unit}`}
                        className="w-20 rounded border px-2 py-1 text-center disabled:bg-gray-100"
                      />
                      <div className="text-xs text-gray-500">of {row.count}</div>
                    </td>
                    <td className="px-3 py-2 text-right">{row.mrp || '-'}</td>
                    <td className="px-3 py-2 text-right">{row.price || '-'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="flex flex-col gap-2 border-t p-4 md:flex-row md:items-center md:justify-end">
          {countError && <p role="alert" className="text-sm text-red-700">{countError}</p>}
          {errorMessage && <p role="alert" className="text-sm text-red-700">{errorMessage}</p>}
          <button
            type="button"
            onClick={onPreview}
            disabled={busy || Boolean(countError) || !selectedCount}
            className="rounded-lg border border-blue-300 px-4 py-2 text-sm font-semibold text-blue-700 hover:bg-blue-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Preview
          </button>
          <button
            type="button"
            onClick={onPrint}
            disabled={busy || Boolean(countError) || !selectedCount}
            className="rounded-lg bg-sky-600 px-4 py-2 text-sm font-semibold text-white hover:bg-sky-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Print Selected
          </button>
        </div>
      </div>
    </div>
  );
}
